// @ts-strict-ignore
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import _ from 'lodash';
import { Icon } from '@seeqdev/qomponents';
import SelectItem from '@/core/SelectItem.organism';
import { AddFormulaParameterModal } from '@/formula/AddFormulaParameterModal.molecule';
import { EditableText } from '@/core/EditableText.atom';
import { warnToast } from '@/utilities/toast.utilities';
import { isItemRedacted } from '@/utilities/redaction.utilities';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { sqWorksheetStore } from '@/core/core.stores';
import { MIN_FORMULA_PANEL_WIDTH } from '@/tools/formula/formulaTool.constants';
import { FORMULA_HELP_WIDTH } from '@/tools/formula/FormulaTool.organism';
import { sqItemsApi } from '@/sdk';

const ITEM_SELECTOR_WIDTH = 200;
const PADDING_AND_ICON_WIDTH = 145;
const APPROXIMATE_SIZE_OF_A_CHARACTER_IN_FORMULA_NAME = 7;
const THREE_DOTS_CHARACTER_NUMBER = 3;
const AREA_OUTSIDE_NAME_INPUT = FORMULA_HELP_WIDTH + ITEM_SELECTOR_WIDTH + PADDING_AND_ICON_WIDTH;

export interface FormulaEditorParam {
  name: string;
  item: { id: string; name: string; type?: string; contextConditionId?: string };
  identifier?: string;
}

interface FormulaParametersTableProps {
  resizeEnabled?: boolean;
  parameters: FormulaEditorParam[];
  updateParameterCallback: (updated: FormulaEditorParam, originalParam: FormulaEditorParam) => void;
  removeParameterCallback: (identifier: string) => void;
  insertParameter?: (param: string) => void;
  additionalItems?: any[];
  excludeStoreItems?: boolean;
  onItemSelect: (idx: any, item: any) => void;
  includeAddToDisplayPane?: boolean;
  allowEditParameterName?: boolean;
  suppressAddOrRemoveOrChangeParameter?: boolean;
  addFormulaParameter?: (params: { name: any; item: any; identifier: any }) => void;
}

export const FormulaParametersTable: React.FunctionComponent<FormulaParametersTableProps> = ({
  resizeEnabled = true,
  additionalItems,
  insertParameter,
  parameters,
  onItemSelect,
  excludeStoreItems,
  updateParameterCallback,
  removeParameterCallback,
  includeAddToDisplayPane = true,
  allowEditParameterName = true,
  suppressAddOrRemoveOrChangeParameter = false,
  addFormulaParameter,
}) => {
  const { t } = useTranslation();
  const [showItemModal, setShowItemModal] = useState(undefined);
  const panelDisplayWidth = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.displayWidth);
  const iconClass = resizeEnabled ? 'ml10' : 'ml5';
  const formulaToolPanelIsSmall = (resizeEnabled && panelDisplayWidth === MIN_FORMULA_PANEL_WIDTH) || !resizeEnabled;

  const charLimitForExtendedVariableName =
    (panelDisplayWidth - AREA_OUTSIDE_NAME_INPUT) / APPROXIMATE_SIZE_OF_A_CHARACTER_IN_FORMULA_NAME -
    THREE_DOTS_CHARACTER_NUMBER;

  const updateParameter = (updatedParameter: FormulaEditorParam, originalParameter: FormulaEditorParam) => {
    if (
      updatedParameter.name !== originalParameter.name &&
      _.some(parameters as any, { identifier: updatedParameter.identifier })
    ) {
      warnToast({ messageKey: 'FORMULA.VARIABLE_UNIQUE' });
    } else {
      updateParameterCallback(updatedParameter, originalParameter);
    }
  };

  const contextParameters = parameters.filter((param) => param.item.contextConditionId);
  const onlyIds = parameters.map((param) => param.item.id);
  const needSpacingForContextButton =
    contextParameters.length > 0 &&
    contextParameters.filter((withContext) => onlyIds.includes(withContext.item.contextConditionId)).length !==
      contextParameters.length;

  return (
    <>
      <table className="table table-striped table-condensed mb3">
        <thead>
          <tr>
            <th className="min-width-47">{t('NAME')}</th>
            <th className="max-width-370">{t('ITEM')}</th>
            <th className="width-minimum" />
          </tr>
        </thead>
        <tbody>
          {_.isEmpty(parameters) && (
            <tr className="text-center text-italic">
              <td colSpan={3} className="flexCenter flexFill">
                {t('FORMULA.NO_VARIABLES')}
              </td>
            </tr>
          )}
          {_.map(parameters, (parameter, idx) => (
            <tr key={parameter.identifier} data-testid={`formulaParameter_${idx}`}>
              <td className="text-valign-middle flexColumnContainer">
                <strong>$</strong>
                <EditableText
                  testId="rowName"
                  value={parameter.identifier}
                  allowEditing={allowEditParameterName}
                  inputClasses={classNames(
                    'flexFill text-bold',
                    formulaToolPanelIsSmall ? 'width-70' : `max-width-${panelDisplayWidth - AREA_OUTSIDE_NAME_INPUT}`,
                  )}
                  textClasses={classNames(
                    'textAlignLeft text-bold',
                    formulaToolPanelIsSmall ? 'width-70' : `max-width-${panelDisplayWidth - AREA_OUTSIDE_NAME_INPUT}`,
                  )}
                  maxDisplayChars={formulaToolPanelIsSmall ? 10 : charLimitForExtendedVariableName}
                  onUpdate={(identifier) => updateParameter({ ...parameter, identifier, name: identifier }, parameter)}
                />
              </td>
              <td className={classNames(resizeEnabled ? `min-width-${ITEM_SELECTOR_WIDTH}` : 'flexFill')}>
                {resizeEnabled && !suppressAddOrRemoveOrChangeParameter ? (
                  <SelectItem
                    testId={`select_${idx}`}
                    selectedItemId={parameter.item.id}
                    excludeStoreItems={excludeStoreItems}
                    showAddToDisplayPane={includeAddToDisplayPane}
                    additionalItems={additionalItems}
                    paramIndex={`${idx}`}
                    identifier={parameter.identifier}
                    extraClassNames={`select_${parameter.name}`}
                    onSelect={onItemSelect}
                    selectByIdAndGroup={true}
                  />
                ) : (
                  <span>
                    {isItemRedacted(parameter.item) && (
                      <Icon
                        tooltip={t('NO_ITEM_ACCESS')}
                        icon="fa-exclamation-triangle"
                        extraClassNames="sq-text-danger"
                      />
                    )}
                    {parameter.item.name}
                  </span>
                )}
              </td>
              <td className="nowrap text-valign-middle">
                {/* eslint-disable-next-line no-nested-ternary */}
                {parameter.item.contextConditionId &&
                parameters.filter((param) => param.item.id === parameter.item.contextConditionId).length === 0 ? (
                  <Icon
                    icon="fa-comment-dots"
                    testId={`contextParam_${parameter.identifier}`}
                    extraClassNames={iconClass}
                    onClick={() => {
                      sqItemsApi.getItemAndAllProperties({ id: parameter.item.contextConditionId }).then(({ data }) =>
                        addFormulaParameter({
                          item: data,
                          name: data.name,
                          identifier: `${parameter.identifier}_context`,
                        }),
                      );
                    }}
                    tooltip={t('FORMULA.ADD_CONTEXT_CONDITION')}
                  />
                ) : needSpacingForContextButton ? (
                  <i className={resizeEnabled ? 'ml23' : 'ml18'} />
                ) : null}
                <Icon
                  icon="fa-plus"
                  testId={`insertParam_${parameter.identifier}`}
                  extraClassNames={iconClass}
                  onClick={() => insertParameter(`$${parameter.identifier}`)}
                  tooltip={t('ADD')}
                />
                {!suppressAddOrRemoveOrChangeParameter && (
                  <Icon
                    icon="fa-pencil"
                    testId={`editParam_${parameter.identifier}`}
                    extraClassNames={iconClass}
                    onClick={() => setShowItemModal(parameter)}
                    tooltip={t('EDIT')}
                  />
                )}
                {!suppressAddOrRemoveOrChangeParameter && (
                  <Icon
                    icon="fa-remove"
                    testId={`removeParam_${parameter.identifier}`}
                    extraClassNames={iconClass}
                    onClick={() => removeParameterCallback(parameter.identifier)}
                    tooltip={t('REMOVE')}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showItemModal && (
        <AddFormulaParameterModal
          parameters={parameters}
          parameter={showItemModal}
          onSave={updateParameter}
          onClose={() => setShowItemModal(undefined)}
          testId="functionParamModal"
        />
      )}
    </>
  );
};
export default FormulaParametersTable;
